import type { ResultTypeKey } from "./results";

export const AD_CATEGORY_KEYS = [
  "books",
  "counseling",
  "career",
  "fortune",
  "matching",
  // 文脈汎用枠。結果タイプへは対応付けず、専門カテゴリの在庫が無いときの
  // fallbackとしてだけ表示される。
  "general",
] as const;

export type AdCategoryKey = (typeof AD_CATEGORY_KEYS)[number];

export type AdCreative = Readonly<{
  id: string;
  category: AdCategoryKey;
  sponsorName: string;
  headline: string;
  body: string;
  linkUrl: string;
  // ASP(A8.net)のインプレッション計測画像。省略時はビーコンを描画しない。
  impressionUrl?: string;
}>;

export const ANALYTICS_AD_CREATIVE_IDS = [
  "coconala-phone-uranai-2026-07",
  "coconala-counseling-2026-07",
  "kimochi-2026-07",
  "it-consultant-bank-2026-07",
  "rakuten-ichiba-2026-07",
] as const;

export type AnalyticsAdCreativeId = (typeof ANALYTICS_AD_CREATIVE_IDS)[number];

export const isAnalyticsAdCreativeId = (
  value: unknown,
): value is AnalyticsAdCreativeId =>
  typeof value === "string" &&
  (ANALYTICS_AD_CREATIVE_IDS as readonly string[]).includes(value);

// 医療・外見コンプレックス・消費者金融・18禁はカテゴリ自体を定義しない。
// 各タイプは優先順のカテゴリ列を持ち、全5カテゴリは列挙しない（fallback判定用）。
export const RESULT_TYPE_AD_CATEGORIES = {
  internet_unscarred: ["books", "matching"],
  emotional_power_saver: ["counseling", "books"],
  cynical_watcher: ["counseling", "books"],
  natural_elite: ["books", "career"],
  human_chess_player: ["books", "career"],
  premature_fossil: ["books", "career"],
  permanent_victim: ["counseling", "fortune"],
  logic_pressure_cooker: ["books", "counseling"],
  approval_reformer: ["books", "counseling"],
  early_rogai: ["books", "career"],
  humanity_abandoned: ["counseling", "matching"],
  elite_lecturer: ["books", "career"],
  counterattack_defense: ["counseling", "fortune"],
  natural_controller: ["books", "career"],
  flame_commentator: ["counseling", "books"],
  zero_margin_defense: ["counseling", "career"],
  good_old_days_reserve: ["books", "fortune"],
  instant_mount: ["counseling", "books"],
  emotion_cost_manager: ["career", "counseling"],
  online_executioner: ["counseling", "books"],
  personal_exception: ["books", "career"],
} as const satisfies Readonly<Record<ResultTypeKey, readonly AdCategoryKey[]>>;

// 掲載中の案件。linkUrl・impressionUrlはASP発行のコードから転記し、
// a8matパラメータを変更しない。空にすればPR枠自体を描画しない。
export const AD_INVENTORY: readonly AdCreative[] = [
  {
    id: ANALYTICS_AD_CREATIVE_IDS[0],
    category: "fortune",
    sponsorName: "ココナラ電話占い",
    headline: "テレビや雑誌で話題の【ココナラ】電話占いが１分100円から",
    body: "実力派の占い師に、電話で気軽に悩みを相談できるサービスです。",
    linkUrl: "https://px.a8.net/svt/ejp?a8mat=4B81T8+9ZLU5U+2PEO+BYLJM",
    impressionUrl: "https://www17.a8.net/0.gif?a8mat=4B81T8+9ZLU5U+2PEO+BYLJM",
  },
  {
    id: ANALYTICS_AD_CREATIVE_IDS[1],
    category: "counseling",
    sponsorName: "ココナラ",
    headline: "ココナラでお悩み相談・カウンセリング",
    body: "カウンセラーや聞き手に、匿名のままオンラインで相談できるサービスです。",
    linkUrl: "https://px.a8.net/svt/ejp?a8mat=4B82L0+AMTQR6+2PEO+1BPGPE",
    impressionUrl: "https://www11.a8.net/0.gif?a8mat=4B82L0+AMTQR6+2PEO+1BPGPE",
  },
  {
    id: ANALYTICS_AD_CREATIVE_IDS[2],
    category: "counseling",
    sponsorName: "Kimochi",
    headline: "公認心理師にオンラインで相談できる「Kimochi」",
    body: "公認心理師だけが登録するオンライン心理カウンセリングサービスです。",
    linkUrl: "https://px.a8.net/svt/ejp?a8mat=4B82L0+A1E4Z6+5OI8+5YZ76",
    impressionUrl: "https://www16.a8.net/0.gif?a8mat=4B82L0+A1E4Z6+5OI8+5YZ76",
  },
  {
    id: ANALYTICS_AD_CREATIVE_IDS[3],
    category: "career",
    sponsorName: "IT Consultant Bank",
    headline: "フリーのIT・SAPコンサルタント向け案件紹介",
    body: "経験に合うコンサルティング案件を探せるプラットフォームです。",
    linkUrl: "https://px.a8.net/svt/ejp?a8mat=4B81T8+50ED2Q+4SXU+HV7V6",
    impressionUrl: "https://www10.a8.net/0.gif?a8mat=4B81T8+50ED2Q+4SXU+HV7V6",
  },
  {
    id: ANALYTICS_AD_CREATIVE_IDS[4],
    category: "general",
    sponsorName: "楽天市場",
    headline: "楽天市場で探す",
    body: "本から日用品まで幅広く揃うショッピングモールです。",
    linkUrl:
      "https://rpx.a8.net/svt/ejp?a8mat=4B81T8+2W6VN6+2HOM+6C1VM&rakuten=y&a8ejpredirect=http%3A%2F%2Fhb.afl.rakuten.co.jp%2Fhgc%2F0ea62065.34400275.0ea62066.204f04c0%2Fa26071635608_4B81T8_2W6VN6_2HOM_6C1VM%3Fpc%3Dhttp%253A%252F%252Fwww.rakuten.co.jp%252F%26m%3Dhttp%253A%252F%252Fm.rakuten.co.jp%252F",
    impressionUrl: "https://www19.a8.net/0.gif?a8mat=4B81T8+2W6VN6+2HOM+6C1VM",
  },
];

// 同一カテゴリに複数在庫があるとき、結果タイプごとに決定的に露出を分散する。
const stableIndex = (key: string, size: number): number => {
  let hash = 0;
  for (const character of key) {
    hash = (hash * 31 + (character.codePointAt(0) ?? 0)) % 9973;
  }
  return hash % size;
};

export const selectAdCreative = (
  resultType: ResultTypeKey,
  inventory: readonly AdCreative[] = AD_INVENTORY,
): AdCreative | null => {
  for (const category of RESULT_TYPE_AD_CATEGORIES[resultType]) {
    const matches = inventory.filter((entry) => entry.category === category);
    if (matches.length > 0) {
      return matches[stableIndex(resultType, matches.length)]!;
    }
  }
  // 文脈一致しない専門カテゴリの在庫は出さず、fallbackはgeneral専用とする。
  const fallback = inventory.filter((entry) => entry.category === "general");
  if (fallback.length === 0) return null;
  return fallback[stableIndex(resultType, fallback.length)]!;
};
